import React, { useCallback, useState } from 'react'

import { SellersToBookResponse, ShippingPrice } from '@hermes/types'

import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import keyWithCookieSuffix from '@lib/utils/keyWithCookieSuffix'

import { HelthjemLogo, PostnordLogo } from '@hermes/web-components'
import { useLocale } from '@hooks/useLocale'
import { useCheckoutStore } from '@lib/stores/checkout'
import ChooseDeliveryServiceModal, {
  ChooseDeliveryServiceData,
  DeliverServices
} from '../components/ChoosedeliveryServiceModal'
import useTranslateMessage from './useTranslateMessage'

interface ChooseDeliveryServiceProps {
  onCloseFn: (d: SellersToBookResponse['data'][number]) => void
  shippingPrice: ShippingPrice
}

const useChooseDeliveryService = ({ onCloseFn, shippingPrice }: ChooseDeliveryServiceProps) => {
  const locale = useLocale()
  const [value, setValue] = useState(DeliverServices.POSTNORD)
  const [localIsOpen, setLocalIsOpen] = useState(false)
  const [closeFnData, setCloseFnData] = useState<SellersToBookResponse['data'][number] | undefined>(undefined)
  const [data, setData] = useState<ChooseDeliveryServiceData>([])
  const { t, n } = useTranslateMessage()

  const region = getRegionFromLocale(locale)
  const setShippingProvider = useCheckoutStore((state) => state.setShippingProvider)

  // This should ideally come from backend
  const DeliverServiceCountryConfig = {
    no: [
      {
        value: DeliverServices.HELTHJEM,
        price: shippingPrice.price,
        deliveryDays: '3-4',
        label: `Helthjem • ${n(shippingPrice.price)}`,
        logo: <HelthjemLogo />,
        description: t(
          { id: 'chooseDelivery.helthjem.subtitle', defaultMessage: 'Delivered within {days} days' },
          { days: '5-7' }
        )
      }
    ],
    se: [
      {
        value: DeliverServices.POSTNORD,
        price: shippingPrice.price,
        deliveryDays: '5-7',
        label: `Postnord • ${n(shippingPrice.price)}`,
        logo: <PostnordLogo />,
        description: t(
          { id: 'chooseDelivery.postnord.subtitle', defaultMessage: 'Delivered within {days} days' },
          { days: '5-7' }
        )
      }
      // {
      //   value: DeliverServices.DOORIS,
      //   price: shippingPrice.price,
      //   deliveryDays: '5-7',
      //   label: t(
      //     { id: 'chooseDelivery.earlyBird.defaultProductName', defaultMessage: 'Early Bird • {price}' },
      //     { price: n(shippingPrice.price) }
      //   ),
      //   logo: <DoorisLogo />,
      //   description: t(
      //     { id: 'chooseDelivery.earlyBird.subtitle', defaultMessage: 'Delivered within {days} days' },
      //     { days: '5-7' }
      //   )
      // }
    ]
  }

  const handleClose = useCallback(() => {
    setValue(DeliverServices.POSTNORD)
    setLocalIsOpen(false)
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value as DeliverServices)
  }

  const setLocalStorage = (v: DeliverServices) => {
    localStorage.setItem(keyWithCookieSuffix('deliveryService', locale), String(v))
  }

  const handleSubmit = () => {
    setLocalStorage(value)
    setShippingProvider(value)

    handleClose()
    if (!closeFnData) {
      throw new Error('Close function data not provided')
    }
    onCloseFn(closeFnData)
  }

  const setIsOpen = (d: SellersToBookResponse['data'][number]) => {
    if (DeliverServiceCountryConfig[region].length > 1) {
      setCloseFnData(d)
      setData(DeliverServiceCountryConfig[region])
      setLocalIsOpen(true)
      return
    }

    setLocalStorage(DeliverServiceCountryConfig[region][0].value)
    setShippingProvider(DeliverServiceCountryConfig[region][0].value)
    return onCloseFn(d)
  }

  return {
    ChooseDeliveryServiceModal: () => (
      <ChooseDeliveryServiceModal
        data={data}
        currentValue={value}
        isOpen={localIsOpen}
        handleClose={handleClose}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
    ),
    setIsOpen,
    shippingProvider: value
  }
}

export default useChooseDeliveryService
